import type { UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { ArrowLeft, KeyRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { deriveApiKey, type ApiKeyFormInput } from '../schema';

interface ApiKeyFormProps {
  mode: 'create' | 'update';
  form: UseFormReturn<ApiKeyFormInput>;
  onSubmit: (input: ApiKeyFormInput) => void;
  isPending: boolean;
}

export function ApiKeyForm({ mode, form, onSubmit, isPending }: ApiKeyFormProps) {
  const secretKey = form.watch('secretKey');
  const apikey = form.watch('apikey');

  const handleGenerate = () => {
    if (!secretKey || secretKey.length < 8) {
      form.setError('secretKey', {
        type: 'manual',
        message: 'Enter at least 8 characters before generating.',
      });
      return;
    }
    form.clearErrors('secretKey');
    form.setValue('apikey', deriveApiKey(secretKey), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <ScrollPage>
      <PageHeader
        title={mode === 'create' ? 'Create API Key' : 'Update API Key'}
        description={
          mode === 'create'
            ? 'Generate a deterministic API key from a secret + IP pair.'
            : 'Edit the IP / regenerate the API key for an existing entry.'
        }
        actions={
          <Button asChild variant="outline">
            <Link to="/user-api-key-list">
              <ArrowLeft className="h-4 w-4" />
              Back to keys
            </Link>
          </Button>
        }
      />

      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <Card>
            <CardHeader>
              <CardTitle>Secret</CardTitle>
              <CardDescription>
                The secret seeds the deterministic key generator. Save it somewhere safe — it
                cannot be recovered later.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="secretKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Secret key</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoFocus={mode === 'create'}
                        placeholder="Pick a long random string"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ip"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Allowed IP / host</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 10.0.0.21" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>API key</CardTitle>
              <CardDescription>
                Generated from the secret above. Click Generate to refresh.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <FormField
                control={form.control}
                name="apikey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API key</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        readOnly
                        placeholder="Click Generate to derive a key"
                        className="font-mono text-xs"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleGenerate}
                disabled={!secretKey}
              >
                <KeyRound className="h-4 w-4" />
                {apikey ? 'Re-generate key' : 'Generate key'}
              </Button>
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to="/user-api-key-list">Cancel</Link>
            </Button>
            <Button type="submit" disabled={isPending || !apikey}>
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === 'create' ? 'Create API key' : 'Save changes'}
            </Button>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}
